import axiosInstance from "@/axios/axiosInstance";
import { GetProfileResponse, ProfileData } from "@/interface/profile.types";

// Extract a user-friendly error message from API error formats
const parseApiErrorMessage = (error: any): string => {
  const errorMessage = error?.response?.data?.errors?.[0]?.message;
  if (errorMessage) return errorMessage;

  const fallbackMessage = "Failed to load profile data";
  const anyError = error as unknown as { response?: { data?: unknown } };
  const data = anyError?.response?.data ?? anyError;

  if (!data) return fallbackMessage;
  if (typeof data === "string") return data || fallbackMessage;

  // Common API shape: { message: string, errors: Array<{ message | field }> }
  try {
    const anyData = data as {
      message?: string;
      errors?: Array<{ message?: string; field?: string }>;
    };
    const message = anyData?.message as string | undefined;
    const errors = Array.isArray(anyData?.errors) ? anyData.errors : [];
    const firstError = (errors?.[0] ?? {}) as {
      message?: string;
      field?: string;
    };
    const firstErrorMessage =
      // prefer explicit message
      (firstError?.message as string | undefined) ||
      // sometimes APIs put message in field when not tied to a real field
      (firstError?.field as string | undefined);

    return firstErrorMessage || message || fallbackMessage;
  } catch (_e) {
    return fallbackMessage;
  }
};

export const getProfile = async (): Promise<ProfileData> => {
  try {
    const response = await axiosInstance.get<GetProfileResponse>(
      "/Profile/Personal-Info"
    );
    return response.data.data;
  } catch (error) {
    // Parse error message but don't show toast here - let the hook handle it
    const errorMessage = parseApiErrorMessage(error);
    throw new Error(errorMessage);
  }
};

export const updateFirstLoginFlag = async (
  isFirstLogin: boolean
): Promise<void> => {
  try {
    await axiosInstance.put(`/Profile/UpdateFirstLoginFlag`, {
      isFirstLogin,
    });
  } catch (error) {
    const message = parseApiErrorMessage(error);
    throw new Error(message);
  }
};

export const restoreAccount = async (): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await axiosInstance.post<{ success: boolean; message: string }>(
      "/Profile/RestoreAccount"
    );
    return response.data;
  } catch (error) {
    const message = parseApiErrorMessage(error);
    throw new Error(message);
  }
};

// Legacy function for backward compatibility (can be removed after migration)
export const loginUser = async (data: any) => {
  try {
    const payload = {
      email: data.email,
      password: data.password,
    };
    const response = await axiosInstance.post(`/auth/login`, payload);
    return response?.data?.data;
  } catch (error) {
    throw error;
  }
};
